import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, qs, type MovedPartsResponse } from '../api';
import { Badge, Button, Card, Empty, ErrorMessage, Field, Input, PageHeader, Select, Spinner, Stat, number, unitMoney } from '../ui';

type Guide = 'sold' | 'stock' | 'both';

const GUIDE_LABEL: Record<MovedPartsResponse['rows'][number]['guideType'], string> = {
  sold: 'Sold',
  stock: 'For sale now',
};

/** A signed percentage with a true minus sign, so a fall reads as a fall. */
function signedPct(pct: number): string {
  const sign = pct > 0 ? '+' : pct < 0 ? '−' : '';
  return `${sign}${Math.abs(pct).toFixed(1)}%`;
}

/** A signed money change, to the penny. */
function signedMoney(value: number, currency: string | null): string {
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${sign}${unitMoney(Math.abs(value), currency ?? 'GBP')}`;
}

/**
 * Which parts' market price has moved. BrickLink's price guide is a rolling six-month window of what people
 * actually paid, so the same part seen month after month shows the market drifting. A part that got dearer
 * while you were out of it is worth buying again; a part that got cheaper is not.
 */
export function PriceMovers() {
  const [guide, setGuide] = useState<Guide>('sold');
  const [minChangePct, setMinChangePct] = useState(5);
  const [days, setDays] = useState(365);

  const query = useQuery({
    queryKey: ['price-movers', guide, minChangePct, days],
    queryFn: () =>
      api<MovedPartsResponse>(
        `/api/v1/market/moved${qs({
          guideType: guide === 'both' ? undefined : guide,
          minChangePct,
          days,
          limit: 200,
        })}`,
      ),
  });

  const rows = query.data?.rows ?? [];
  const risers = rows.filter((r) => r.changePct > 0);
  const fallers = rows.filter((r) => r.changePct < 0);
  const biggestRiser = risers.length > 0 ? risers.reduce((a, b) => (b.changePct > a.changePct ? b : a)) : null;
  const biggestFaller = fallers.length > 0 ? fallers.reduce((a, b) => (b.changePct < a.changePct ? b : a)) : null;

  return (
    <>
      <PageHeader
        title="Price movers"
        subtitle="Parts whose BrickLink market price has moved most over the time held. A rising price is a part worth buying again; a falling one is not."
      />
      <div className="space-y-4">
        <Card>
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Guide" className="w-48" hint="Sold is what people actually paid.">
              <Select value={guide} onChange={(e) => setGuide(e.target.value as Guide)}>
                <option value="sold">Sold (6 months)</option>
                <option value="stock">For sale now</option>
                <option value="both">Both</option>
              </Select>
            </Field>
            <Field label="Minimum move (%)" className="w-32">
              <Input
                type="number"
                min={0}
                max={100}
                step={1}
                value={minChangePct}
                onChange={(e) => setMinChangePct(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
              />
            </Field>
            <Field label="Days" className="w-28" hint="How far back to look.">
              <Input
                type="number"
                min={1}
                max={3650}
                step={1}
                value={days}
                onChange={(e) => setDays(Math.max(1, Math.min(3650, Number(e.target.value) || 1)))}
              />
            </Field>
            <Button variant="secondary" onClick={() => void query.refetch()}>
              Refresh
            </Button>
          </div>
        </Card>

        <div className="grid gap-3 sm:grid-cols-3">
          <Stat
            label="Parts moved"
            value={number(rows.length)}
            note={rows.length > 0 ? `Moved ${Math.abs(minChangePct)}% or more over ${number(days)} days.` : undefined}
          />
          <Stat
            label="Biggest riser"
            value={biggestRiser ? signedPct(biggestRiser.changePct) : '—'}
            note={biggestRiser ? `${biggestRiser.itemNo} · ${biggestRiser.colorName}` : 'Nothing has risen yet.'}
          />
          <Stat
            label="Biggest faller"
            value={biggestFaller ? signedPct(biggestFaller.changePct) : '—'}
            note={biggestFaller ? `${biggestFaller.itemNo} · ${biggestFaller.colorName}` : 'Nothing has fallen yet.'}
          />
        </div>

        <Card>
          {query.isPending && <Spinner />}
          {query.error && <ErrorMessage error={query.error} />}
          {query.data && rows.length === 0 && (
            <Empty>
              No part has moved that much yet. The history is built up nightly from the BrickLink price guide, so a
              part needs a few nights of figures before it can show a trend.
            </Empty>
          )}
          {rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="data w-full min-w-[52rem] text-sm">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Colour</th>
                    <th>Cond.</th>
                    <th>Guide</th>
                    <th className="text-right">Oldest avg</th>
                    <th className="text-right">Newest avg</th>
                    <th className="text-right">Change</th>
                    <th className="text-right">Change %</th>
                    <th className="text-right">Sightings</th>
                    <th>Direction</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const rising = r.changePct > 0;
                    const falling = r.changePct < 0;
                    return (
                      <tr key={`${r.itemId}-${r.colorId}-${r.condition}-${r.guideType}`}>
                        <td>
                          <Link to={`/items/${r.itemId}?tab=price-history`} className="font-medium hover:underline">
                            {r.itemNo}
                          </Link>
                          <div className="max-w-56 truncate text-xs text-slate-500">{r.itemName}</div>
                        </td>
                        <td className="whitespace-nowrap">{r.colorName}</td>
                        <td className="whitespace-nowrap">{r.condition === 'N' ? 'New' : 'Used'}</td>
                        <td className="whitespace-nowrap">{GUIDE_LABEL[r.guideType]}</td>
                        <td className="text-right tabular-nums">{unitMoney(r.earliestAvg, r.currency ?? 'GBP')}</td>
                        <td className="text-right tabular-nums">{unitMoney(r.latestAvg, r.currency ?? 'GBP')}</td>
                        <td className="text-right tabular-nums">{signedMoney(r.change, r.currency)}</td>
                        <td className={`text-right tabular-nums font-medium ${rising ? 'text-emerald-700' : falling ? 'text-red-700' : 'text-slate-600'}`}>
                          {signedPct(r.changePct)}
                        </td>
                        <td className="text-right tabular-nums">{number(r.points)}</td>
                        <td>
                          {rising && <Badge tone="green">Rising</Badge>}
                          {falling && <Badge tone="red">Falling</Badge>}
                          {!rising && !falling && <Badge tone="slate">Level</Badge>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
